import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClients, type Client } from '../../clients/useData';
import { formatDateShort } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { listRowClass } from '../themeHelpers';
import GenericListWidget from './GenericListWidget';

export interface RecentClientsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentClients: React.FC<RecentClientsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { can } = useAuth();
  const canViewClients = can('clients', 'view');
  const { clients, isLoading } = useClients(undefined, canViewClients);

  const items = React.useMemo(() => {
    const arr = Array.isArray(clients) ? clients : [];
    return [...arr]
      .sort((a: Client, b: Client) => {
        const ta = new Date(a.clientCreatedAt || 0).getTime();
        const tb = new Date(b.clientCreatedAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [clients, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<Client>
      title="Recent organizations"
      viewAllPath="/clients"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No clients"
      getItemKey={(c) => c.clientId}
      embedded={embedded}
      renderItem={(client) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/clients/${client.clientId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/clients/${client.clientId}`)
          }
          className={listRowClass(colors)}
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {client.clientName || `Organization #${client.clientId}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {client.clientCreatedAt ? formatDateShort(client.clientCreatedAt) : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentClients;
